/**
 * ProposalQualityGate
 * Client-side quality scoring for AI-generated proposals.
 * Used on the AI Proposal Writer page to give freelancers an
 * objective quality signal before they send a proposal and to
 * gate the "looks good" action until a minimum bar is met.
 *
 * The scoring is intentionally deterministic and local — it does
 * not call the backend so freelancers get instant feedback as they
 * iterate. The rubric mirrors the factors our top-rated proposals
 * exhibit in historical data: length, specificity, personalization,
 * skill coverage, clear CTA, and absence of generic filler.
 */

import React, { useMemo } from "react";
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  ShieldCheck,
  Gauge,
} from "lucide-react";

const GENERIC_PHRASES = [
  "i am the best",
  "i am perfect for this",
  "i can do anything",
  "please hire me",
  "i am interested in this job",
  "i have years of experience",
  "feel free to contact me",
  "looking forward to hearing from you",
];

const CTA_MARKERS = [
  "let's",
  "lets ",
  "schedule",
  "call",
  "hop on",
  "discuss",
  "jump on",
  "meeting",
  "next step",
  "send you",
  "share a ",
  "follow up",
];

const SPECIFIC_MARKERS = [
  /\b\d+\s*(years|yrs|months|weeks)\b/i,
  /\$\s*\d+/,
  /\b\d+\s*%/,
  /\b(increased|reduced|shipped|launched|migrated|built|scaled|automated)\b/i,
];

/**
 * Compute a 0-100 quality score with per-factor feedback.
 * @param {object} args
 * @param {string} args.proposal Generated proposal text
 * @param {string} args.jobTitle
 * @param {string} args.jobDescription
 * @param {string} args.jobSkills Comma-separated skills
 * @returns {{score:number, band:string, checks:Array<{id:string,label:string,passed:boolean,weight:number,hint?:string}>}}
 */
export function scoreProposal({
  proposal = "",
  jobTitle = "",
  jobDescription = "",
  jobSkills = "",
}) {
  const text = (proposal || "").trim();
  const lower = text.toLowerCase();
  const wordCount = text ? text.split(/\s+/).filter(Boolean).length : 0;

  const skills = jobSkills
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);

  const skillsMentioned = skills.filter((s) => s && lower.includes(s));
  const skillCoverage = skills.length
    ? skillsMentioned.length / skills.length
    : 0;

  // Job title words over 4 chars that appear in the proposal
  const titleKeywords = (jobTitle || "")
    .toLowerCase()
    .split(/\s+/)
    .filter((w) => w.length > 4);
  const titleReferenced =
    titleKeywords.length === 0 ||
    titleKeywords.some((w) => lower.includes(w));

  // Specific / quantified phrases
  const specificMatches = SPECIFIC_MARKERS.filter((re) => re.test(text)).length;

  // Generic filler detection
  const genericHits = GENERIC_PHRASES.filter((p) => lower.includes(p));

  // CTA detection
  const hasCta = CTA_MARKERS.some((m) => lower.includes(m));

  // Opening personalization: does the first 120 chars reference the job?
  const opening = lower.slice(0, 160);
  const openingPersonalized =
    titleKeywords.some((w) => opening.includes(w)) ||
    skillsMentioned.some((s) => opening.includes(s));

  // Avoid starting with "I" ego-opening
  const firstWord = (text.split(/\s+/)[0] || "").toLowerCase();
  const nonEgoOpening = firstWord !== "i" && firstWord !== "i'm" && firstWord !== "im";

  const checks = [
    {
      id: "length",
      label: "Length is in the sweet spot (150–400 words)",
      passed: wordCount >= 150 && wordCount <= 400,
      weight: 15,
      hint:
        wordCount < 150
          ? `Only ${wordCount} words — expand with context, a concrete example, and a clear next step.`
          : wordCount > 400
          ? `${wordCount} words is too long. Trim to the strongest ~250.`
          : undefined,
    },
    {
      id: "skills",
      label: "Mentions the required skills",
      passed: skills.length === 0 ? true : skillCoverage >= 0.5,
      weight: 20,
      hint:
        skills.length && skillCoverage < 0.5
          ? `Only referenced ${skillsMentioned.length}/${skills.length} required skills. Weave in more of: ${skills
              .filter((s) => !skillsMentioned.includes(s))
              .slice(0, 5)
              .join(", ")}.`
          : undefined,
    },
    {
      id: "title",
      label: "References the job/role",
      passed: titleReferenced,
      weight: 10,
      hint: titleReferenced
        ? undefined
        : "Call out the role or project name in the first paragraph so the client knows you read the brief.",
    },
    {
      id: "specificity",
      label: "Concrete, quantified examples",
      passed: specificMatches >= 1,
      weight: 15,
      hint:
        specificMatches >= 1
          ? undefined
          : "Add a concrete number — years of experience, shipped features, % improvements — to prove you've done this.",
    },
    {
      id: "opening",
      label: "Personalized opening (not a generic intro)",
      passed: openingPersonalized && nonEgoOpening,
      weight: 15,
      hint:
        openingPersonalized && nonEgoOpening
          ? undefined
          : "Open with something specific about the client's project instead of starting with \"I am...\".",
    },
    {
      id: "cta",
      label: "Clear call-to-action at the end",
      passed: hasCta,
      weight: 10,
      hint: hasCta
        ? undefined
        : "End with a clear next step — a quick call, a question, or a sample you can send.",
    },
    {
      id: "generic",
      label: "No generic filler phrases",
      passed: genericHits.length === 0,
      weight: 15,
      hint:
        genericHits.length === 0
          ? undefined
          : `Replace generic lines like "${genericHits[0]}" with something specific to this job.`,
    },
  ];

  const totalWeight = checks.reduce((s, c) => s + c.weight, 0);
  const earned = checks.reduce((s, c) => s + (c.passed ? c.weight : 0), 0);
  const score = Math.round((earned / totalWeight) * 100);

  let band = "needs-work";
  if (score >= 85) band = "excellent";
  else if (score >= 70) band = "good";
  else if (score >= 55) band = "fair";

  return { score, band, checks, wordCount };
}

export const MIN_QUALITY_TO_SEND = 70;

const BAND_META = {
  excellent: {
    label: "Excellent",
    color: "text-emerald-600",
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    ring: "ring-emerald-500",
  },
  good: {
    label: "Good",
    color: "text-green-600",
    bg: "bg-green-50",
    border: "border-green-200",
    ring: "ring-green-500",
  },
  fair: {
    label: "Fair — improve before sending",
    color: "text-amber-600",
    bg: "bg-amber-50",
    border: "border-amber-200",
    ring: "ring-amber-500",
  },
  "needs-work": {
    label: "Needs work",
    color: "text-red-600",
    bg: "bg-red-50",
    border: "border-red-200",
    ring: "ring-red-500",
  },
};

export const ProposalQualityGate = ({
  proposal,
  jobTitle,
  jobDescription,
  jobSkills,
  onImproveRequested,
}) => {
  const result = useMemo(
    () => scoreProposal({ proposal, jobTitle, jobDescription, jobSkills }),
    [proposal, jobTitle, jobDescription, jobSkills]
  );

  if (!proposal) return null;

  const meta = BAND_META[result.band];
  const failing = result.checks.filter((c) => !c.passed);

  return (
    <div className={`border-2 ${meta.border} ${meta.bg}`}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-inherit">
        <div className="flex items-center gap-2">
          <Gauge className={`h-4 w-4 ${meta.color}`} />
          <h4 className="text-xs font-medium uppercase tracking-wider text-gray-600">
            Quality Score
          </h4>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-2xl font-light ${meta.color}`}>
            {result.score}
          </span>
          <span className={`text-xs font-medium ${meta.color}`}>
            {meta.label}
          </span>
        </div>
      </div>

      <div className="p-4 space-y-3">
        {/* Bar */}
        <div className="w-full h-1 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-500 ${
              result.band === "excellent"
                ? "bg-emerald-500"
                : result.band === "good"
                ? "bg-green-500"
                : result.band === "fair"
                ? "bg-amber-500"
                : "bg-red-500"
            }`}
            style={{ width: `${result.score}%` }}
          />
        </div>

        {/* Checks list */}
        <ul className="space-y-1.5">
          {result.checks.map((c) => (
            <li
              key={c.id}
              className="flex items-start gap-2 text-xs text-gray-700"
            >
              {c.passed ? (
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 mt-0.5 flex-shrink-0" />
              ) : (
                <XCircle className="h-3.5 w-3.5 text-red-500 mt-0.5 flex-shrink-0" />
              )}
              <div className="flex-1">
                <div className={c.passed ? "text-gray-500" : "text-gray-800 font-medium"}>
                  {c.label}
                </div>
                {!c.passed && c.hint && (
                  <div className="text-gray-500 mt-0.5">{c.hint}</div>
                )}
              </div>
            </li>
          ))}
        </ul>

        {/* Gate footer */}
        {result.score < MIN_QUALITY_TO_SEND ? (
          <div className="flex items-start gap-2 p-3 bg-white border border-amber-200 text-amber-800 text-xs">
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium">
                Quality gate: minimum {MIN_QUALITY_TO_SEND} to send.
              </p>
              <p className="mt-0.5 text-amber-700">
                Fix {failing.length} item{failing.length === 1 ? "" : "s"}{" "}
                above — or use "Improve Proposal" to have AI revise it for you.
              </p>
              {onImproveRequested && (
                <button
                  onClick={() =>
                    onImproveRequested(
                      failing
                        .map((c) => c.hint || c.label)
                        .filter(Boolean)
                        .slice(0, 3)
                        .join(" ")
                    )
                  }
                  className="mt-2 text-xs font-medium underline hover:no-underline"
                >
                  Auto-improve with this feedback →
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 p-3 bg-white border border-emerald-200 text-emerald-800 text-xs">
            <ShieldCheck className="h-4 w-4 flex-shrink-0" />
            <span className="font-medium">
              Ready to send — this proposal clears the quality gate.
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProposalQualityGate;
